import { SPECIFICATIONS } from 'pro/config/pdfs';
import { isPhone } from 'base/utils/client';
import {
    ITopBannerData,
    IKeyFeaturesData,
    IEfficiencyBannerData,
    IFarRedData,
    IOptimizedSpectraData,
    IGreenhouseOptimizedData,
} from './interface';

export const config: {
    topBanner: ITopBannerData;
    keyFeatures: IKeyFeaturesData;
    efficiencyBanner: IEfficiencyBannerData;
    farRed: IFarRedData;
    optimizedSpectra: IOptimizedSpectraData;
    greenhouseOptimized: IGreenhouseOptimizedData;
} = {
    topBanner: {
        banner: isPhone ? {
            width: 375,
            height: 667,
            src: 'asset/width-1500/picture/3988d1a839b80b5d28ecb69b18c651b6.jpg',
            alt: '',
            bgColor: '#000'
        } : {
            width: 1920,
            height: 1023,
            // src: 'asset/width-3840/picture/3bab42bf7c22e8e437aff72431be2b8b.jpg',
            src: 'asset/width-3840/picture/fd4dc413858acef531e119e2e79ff552.jpg',
            alt: '',
            bgColor: '#000'
        },
        title: '<strong>LINEAR TOP LIGHT</strong> <span>SERIES</span>',
        subtitle: 'The Advanced Lighting Solution Purpose-Built for Greenhouse Cultivation',
        contactLinkText: 'GET A QUOTE',
        description: 'Grow smarter and maximize greenhouse performance with precision-engineered top lighting made for crops like tomatoes, cucumbers, peppers.',
    },
    keyFeatures: {
        title: 'Key Features At a Glance',
        list: isPhone ? [
            {
                icon: 'file-asset/file/80432b96d058c9b93ec6796a517e43dc.svg',
                title: 'Up to 4.0 μmol/J PPE Up to 1000 μmol/s BPF'
            },
            {
                icon: 'file-asset/file/a1a2bb081b2283bc394aa7b73dc81229.svg',
                title: 'Slim Design Unblocking Sunlight'
            },
            {
                icon: 'file-asset/file/6b6ac93fab555a46259678335100f5e3.svg',
                title: 'Independent Far Red Control'
            },
            {
                icon: 'file-asset/file/53c40fa8a5f8f56e2e4ec8d13be64cb2.svg',
                title: 'Seamless End-to- End Connection'
            },
            {
                icon: 'file-asset/file/c07808be7da82c90d154a64a7961e267.svg',
                title: 'Optimal Spectra for Greenhouse Crops'
            },
            {
                icon: 'file-asset/file/0ffd3a65ceef9aa32034fa2c626d8022.svg',
                title: 'Plug-and-Play Flexible Mounting'
            }
        ] : [
            {
                icon: 'file-asset/file/80432b96d058c9b93ec6796a517e43dc.svg',
                title: 'Up to 4.0 μmol/J PPE<br>Up to 1000 μmol/s BPF'
            },
            {
                icon: 'file-asset/file/6b6ac93fab555a46259678335100f5e3.svg',
                title: 'Independent Far<br>Red Control'
            },
            {
                icon: 'file-asset/file/c07808be7da82c90d154a64a7961e267.svg',
                title: 'Optimal Spectra for<br>Greenhouse Crops'
            },
            {
                icon: 'file-asset/file/a1a2bb081b2283bc394aa7b73dc81229.svg',
                title: 'Slim Design<br>Unblocking Sunlight'
            },
            {
                icon: 'file-asset/file/53c40fa8a5f8f56e2e4ec8d13be64cb2.svg',
                title: 'Seamless End-to-<br>End Connection'
            },
            {
                icon: 'file-asset/file/0ffd3a65ceef9aa32034fa2c626d8022.svg',
                title: 'Plug-and-Play<br>Flexible Mounting'
            }
        ],
    },

    efficiencyBanner: {
        banner: isPhone ? {
            width: 375,
            height: 499,
            src: 'asset/width-1500/picture/2c65fd0c7e346363ec5eeae995d71569.jpg',
            alt: '',
            bgColor: '#000'
        } : {
            width: 1920,
            height: 1067,
            src: 'asset/width-3840/picture/8d352bb7403f6c09d56d703a55f0741f.jpg',
            alt: '',
            bgColor: '#000'
        },
        title: 'Efficiency That Drives Results',
        description: 'With PPE up to <span class="g-highlight">4.0 µmol/J</span> and BPF up to <span class="g-highlight">1000 µmol/s</span>, the Linear Top Light Series offers elite performance for sun-supplemented greenhouses.',
    },
    farRed: {
        banner: isPhone ? {
            width: 375,
            height: 768,
            src: 'asset/width-1500/picture/8c990f6579077e95dadf4e0631732562.jpg',
            alt: '',
            bgColor: '#000'
        } : {
            width: 1920,
            height: 1232,
            src: 'asset/width-3840/picture/df0ead86d6bac0784c479f3e4d9a9712.jpg',
            alt: '',
            bgColor: '#000'
        },
        title: 'Take Control with Adjustable Far Red',
        description: 'Unlike standard bar LED fixtures, the Linear Series gives growers <span class="g-highlight">independent Far Red control</span>, allowing fine-tuning of light strategies across veg and bloom stages. Adapt lighting to crop needs, photoperiod, and environment—without overhauling your setup.',
        note: {
            title: 'Why Far Red Matters in Greenhouse Crops',
            list: [
                {
                    icon: 'file-asset/file/2593fd737948a2851d868110aa9458ed.svg',
                    title: 'Stem Elongation & Leaf Expansion',
                    description: 'Boost internodal spacing and leaf orientation—key for better airflow, light penetration, and plant structure in crops like tomatoes and cucumbers.'
                },
                {
                    icon: 'file-asset/file/f5f46774a63dc97e6a078c9a1a548a64.svg',
                    title: 'Mimics Natural Sunlight for Shade Avoidance Response',
                    description: 'Simulate natural sunlight shifts and improve flowering speed and stretch—especially useful in low-light seasons or northern latitudes.'
                },
            ]
        }
    },
    optimizedSpectra: {
        title: 'Optimized Spectra, Backed by Research',
        description: 'Our spectra are the result of collaboration with horticulture experts who\'ve spent decades studying crop responses under greenhouse conditions. ',
        pictures: {
            title: 'Available Spectrum Options:',
            width: 566,
            height: 391,
            list: [
                'asset/width-2264/picture/6bfb6186a10e873f818714dda38ce0ce.png',
                'asset/width-2264/picture/e0f9dbc2e4ffa4e2f87b1fb38aeacbee.png'
            ]
        },
        spectraTuned: {
            title: 'Both spectra are fine-tuned to stimulate desired plant responses:',
            list: [
                {
                    icon: 'file-asset/file/7d7dbdcb945a4cd5629f3bc3dfad0b71.svg',
                    title: 'Stem elongation and internodal spacing',
                },
                {
                    icon: 'file-asset/file/53f940d83430781bb667d7b452beb6f5.svg',
                    title: 'Enhanced flowering and fruit set',
                },
                {
                    icon: 'file-asset/file/6ed7c05fd692a42409c77495a9d0f3f0.svg',
                    title: 'Efficient photosynthesis under fluctuating daylight',
                },
            ]
        }
    },
    greenhouseOptimized: {
        banner: isPhone ? {
            width: 375,
            height: 664,
            src: 'asset/width-1500/picture/0e2dba813760957d5abd78cdde1c1a27.jpg',
            alt: '',
            bgColor: '#000'
        } : {
            width: 1920,
            height: 1012,
            src: 'asset/width-3840/picture/c0dbfc8fb90dfbd1fff9bf91202abdd3.jpg',
            alt: '',
            bgColor: '#000'
        },
        title: 'Greenhouse-Optimized Design',
        list: [
            {
                title: 'Slimline Profile',
                description: 'Minimal shadowing to preserve natural sunlight',
            },
            {
                title: 'Built-in Driver, Modular Form',
                description: 'Easy installation & scalable deployment',
            },
            {
                title: 'End-to-End Connectivity',
                description: 'Uniform light spread across long beds',
            },
            {
                title: 'Flexible Mounting',
                description: 'Compatible with most greenhouse structures',
            },
        ],
        download: {
            btnText: 'Download the Specs Sheet',
            ...SPECIFICATIONS['Linear-Top-Light'],
        },
        guarantee: {
            src: 'asset/width-1128/picture/a5af9b238f8a36a44bb8dc5a0ed47985.png',
            width: isPhone ? 225 : 282,
            height: isPhone ? 35 : 44
        },
        contact: {
            title: 'Have a greenhouse project? Tell us your needs,<br/>our team will help you build the perfect lighting solution.',
            btnText: 'CONTACT US'
        }
    },
};